'use client';
import { useCartService } from '@/hooks/cart/useCartService';
import { Button, Flex } from '@chakra-ui/react';
import CartOrderSummary from '../components/CartOrderSummary';
import FreeShippingProgress from '../components/FreeShippingProgress';
import CartProductCard from '../components/CartProductCard/CartProductCard';
import { getAmountToFreeShipping } from '@/utils/domain/cart';
import React, { useMemo } from 'react';
import { MiniCartBag } from '@/components/cart/ShoppingBagDrawer/components/CardCountBag';
import { Condition } from '@/utils/react';
import { TEMP_ITEM_KEY } from '@/infrastructure/cart/utils/queries';
import { DrawerBody, DrawerHeader, DrawerTitle } from '@/components/ui';
import { useRegionCurrency } from '@/contexts/regionCurrencyContext';
import { SafeLink } from '@/components/ui/SafeLink';

export const CartWithItemView = () => {
  const { useGetCart, useDeleteCartItem, useUpdateCartItem } = useCartService();
  const { data } = useGetCart();
  const { mutate: deleteItemMutate } = useDeleteCartItem();
  const { mutate: updateItemMutate } = useUpdateCartItem();
  const { currency } = useRegionCurrency();

  const totalProductQuantity = useMemo(
    () => data.cart.item.reduce((prev, curr) => prev + curr.quantity, 0),
    [data.cart.item],
  );

  const totalProductPrice = useMemo(
    () =>
      data.cart.item.reduce(
        (previousValue, currentValue) => previousValue + currentValue.price.price * currentValue.quantity,
        0,
      ),
    [data.cart.item],
  );

  const handleDeleteItem = (itemKey: string) => {
    deleteItemMutate({
      itemKey,
    });
  };
  const handleUpdateCartItem = ({ quantity, itemKey }: { quantity: number; itemKey: string }) => {
    updateItemMutate({
      itemKey,
      quantity,
    });
  };

  return (
    <>
      <DrawerHeader
        alignItems="center"
        backgroundColor="#f6f7fb"
        display="flex"
        height="72px"
        flexDirection="row"
        justifyContent="space-between"
        padding="10px"
        width="100%"
        paddingRight="20px"
      >
        <DrawerTitle fontWeight="bold" textAlign="center" textStyle="body2" width="100%">
          Shopping Cart
        </DrawerTitle>

        <Flex alignItems="center" justifyContent="center" marginLeft="auto" position="absolute" right={4} width="40px">
          <MiniCartBag quantity={totalProductQuantity} />
        </Flex>
      </DrawerHeader>

      <DrawerBody padding="0" width="100%" overflowX="auto" paddingX="8px">
        <Flex flexDirection="column" height="100%" width="100%">
          {data.cart.item.map((item, index) => (
            <React.Fragment key={item.key}>
              <Condition
                expression={item.name === TEMP_ITEM_KEY}
                then={<CartProductCard.Skeleton />}
                else={
                  <CartProductCard
                    item={item}
                    currency={data.cart.currency.currency}
                    onQuantityChange={(quantity) => {
                      handleUpdateCartItem({
                        itemKey: item.key,
                        quantity: quantity,
                      });
                    }}
                    onRemoveItem={() => handleDeleteItem(item.key)}
                  />
                }
              />

              {index < data.cart.item.length - 1 && (
                <Flex borderBottom="2px solid" borderColor="#a8b3ff4d" width="100%" />
              )}
            </React.Fragment>
          ))}
        </Flex>
      </DrawerBody>
      <Flex backgroundColor="#f6f7fb" flexDirection="column" gap={5} height="fit-content" padding="20px" width="100%">
        <Flex position="relative" flexDirection="column" gap={5}>
          <FreeShippingProgress
            currency={currency}
            amountToFreeShipping={getAmountToFreeShipping({
              currency: currency,
              currentPrice: totalProductPrice * 0.01,
            })}
          />
          <CartOrderSummary
            subtotal={totalProductPrice}
            deliveryFee={data.cart.shippingRates.length > 0 ? data.cart.shippingRates[0].price : 0}
            totalPrice={data.cart.totals.totalPrice}
            totalDiscount={data.cart.totals.totalDiscount}
            currency={data.cart.currency.currency}
            coupons={data.cart.coupons}
            isFreeShipping={data.cart.shippingRates.length === 0 || data.cart.shippingRates[0].price === 0}
          />
        </Flex>
        <SafeLink href="/checkout">
          <Button backgroundColor="neutral.400" borderRadius="8px" size="lg" textTransform="uppercase" width="100%">
            CHECKOUT
          </Button>
        </SafeLink>
      </Flex>
    </>
  );
};
